import type {
  Float8_Comparison_Exp,
  ConditionsStudiesQueryVariables,
} from 'graphql/research/types-and-hooks';
import type { MapConditions } from 'lib/queries/dashboard';
import type { PaginationProps } from './pagination-clientside';
import Image from 'next/image';

import React, { memo, useCallback, useMemo, useReducer } from 'react';
import { useToast, Paragraph } from '@leafwell/components';

import { useConditionsStudiesQuery } from 'graphql/research/types-and-hooks';
import { DASHBOARD_FILTERS, mapConditions } from 'lib/queries/dashboard';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { useDasboardContext } from 'contexts/dashboard-context';

import Pagination from './pagination-clientside';
import ConditionsList from './conditions-list';
import CardEmpty from './card-empty';
import Loader from './loader';
import LinkBadge from './link-badge';
import { useEventsTracker } from 'contexts/eventsTracker';

type PaginatedSystems = {
  conditions: MapConditions;
  offset: number;
  total: number;
};

export type SystemsPaginatedAction =
  | {
      type: 'updateItems';
      value: Pick<PaginatedSystems, 'conditions' | 'total'>;
    }
  | {
      type: 'changeOffset';
      value: number;
    }
  | {
      type: 'reset';
      value: Pick<PaginatedSystems, 'conditions' | 'total'>;
    };

const CONDITIONS_PER_PAGE = 16;

export const paginatedSystemsReducer = (
  state: PaginatedSystems,
  action: SystemsPaginatedAction,
): PaginatedSystems => {
  switch (action.type) {
    case 'updateItems': {
      return {
        ...state,
        ...action.value,
      };
    }
    case 'changeOffset': {
      return {
        ...state,
        offset: action.value,
      };
    }
    case 'reset': {
      return {
        offset: 0,
        ...action.value,
      };
    }
  }
};

const Conditions: React.FC<
  Omit<PaginationProps, 'limit'> & { conditions: MapConditions }
> = memo(({ conditions, ...pagination }) => {
  const { conditionsPage } = useWordPressContext();
  const { trackEvent } = useEventsTracker();

  return conditions.length ? (
    <section className="grid gap-6">
      <ConditionsList<MapConditions[0]>
        {...{
          conditions,
          variant: 'boxed',
          renderItem: ({
            id,
            studies: {
              aggregate: { count },
            },
            name,
            uri,
          }) => (
            <span
              key={id}
              onClick={() =>
                trackEvent({
                  event: 'select_content',
                  content_type: name,
                  item_id: 'medical_condition',
                  origin: 'research_dashboard',
                })
              }
            >
              <LinkBadge
                {...{
                  className: 'flex items-center justify-between gap-x-6',
                  href: uri || conditionsPage.uri,
                  variant: 'secondary',
                  key: id,
                }}
              >
                <span className="grow">{name}</span>
                {count}
                <Image
                  width={15}
                  height={12}
                  src="/arrow.svg"
                  alt=""
                  className="hidden md:block"
                />
              </LinkBadge>
            </span>
          ),
        }}
      />
      <Pagination
        {...{
          className: 'self-end',
          limit: CONDITIONS_PER_PAGE,
          variant: 'minimal',
          ...pagination,
        }}
      />
    </section>
  ) : (
    <CardEmpty className="col-span-full row-start-1 md:self-start" />
  );
});

const DashboardConditions: React.FC<{ id: string }> = ({ id }) => {
  const { addToast } = useToast();
  const { translations } = useWordPressContext();
  const {
    filters,
    initialData: { [DASHBOARD_FILTERS.CONDITIONS]: initialConditions, wp },
    studies: { byYear },
  } = useDasboardContext();
  const getInitalData = useCallback(
    () => ({
      conditions: mapConditions({
        conditions: initialConditions.nodes,
        wpData: wp.conditions,
      }),
      total: initialConditions.pagination.aggregate.count,
    }),
    [wp.conditions, initialConditions],
  );
  const [{ conditions, offset, total }, updateSettings] = useReducer(
    paginatedSystemsReducer,
    {
      offset: 0,
      ...getInitalData(),
    },
  );
  const studyIds = useMemo(() => byYear.map(({ id }) => id), [byYear]);
  const variables: ConditionsStudiesQueryVariables = useMemo(
    () => ({
      offset,
      ...(filters?.[DASHBOARD_FILTERS.CONDITIONS]?.medical_condition_id
        ? {
            conditionIds:
              filters[DASHBOARD_FILTERS.CONDITIONS].medical_condition_id,
          }
        : {}),
      ...(filters
        ? { studyIds: { _in: studyIds } as Float8_Comparison_Exp }
        : {}),
    }),
    [filters?.[DASHBOARD_FILTERS.ORGAN_SYSTEMS], offset, studyIds],
  );

  const showInitialData = useMemo(
    () => !filters && offset === 0,
    [filters, offset],
  );

  const { loading } = useConditionsStudiesQuery({
    skip: showInitialData,
    variables,
    onCompleted: data => {
      updateSettings({
        type: 'updateItems',
        value: {
          conditions: mapConditions({
            conditions: data.nodes,
            wpData: wp.conditions,
          }),
          total: data.pagination.aggregate.count,
        },
      });
    },
    onError: error => {
      addToast({
        content: error.message,
        key: 'dashboard-studies-list-error',
        type: 'negative',
        shouldRemove: true,
      });
    },
  });

  useMemo(() => {
    if (showInitialData) {
      updateSettings({
        type: 'reset',
        value: getInitalData(),
      });
    }
  }, [getInitalData, showInitialData]);

  return (
    <section {...{ id, className: 'grid gap-y-4' }}>
      <Paragraph size="large" tagName="header">
        {translations['Studies by Medical Condition']}
      </Paragraph>
      {loading && offset === 0 ? (
        <Loader fullHeight={false} />
      ) : (
        <Conditions
          {...{
            loading,
            offset,
            onPageChange: offset =>
              updateSettings({
                type: 'changeOffset',
                value: offset,
              }),
            conditions,
            total,
          }}
        />
      )}
    </section>
  );
};

export default DashboardConditions;
